import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import useTranslator from "../../hooks/useTranslator";
import axiosInstance from "../../services/authWrapper";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";
import { TENANT_REQUIREMENTS } from "../../helper/common";
import constants from "../../Utils/constants";
import BeforeSharingModal, {
  APPLICANT_QUESTIONS_STEP,
} from "./BeforeSharingModal";

/**
 * Loads the landlord's tenant requirements (household and pets, KYC-AML)
 * and shows BeforeSharingModal for them.
 *
 * mode "before": shows the open steps; runs `onShare` when the last open
 *   step is done, or on close once the required steps are done (nothing open:
 *   shares straight away).
 * mode "after": the application is already shared; only shows the steps.
 * mode "auto": "after" when the application is already shared, else "before".
 * closable false: mandatory; the flow ends (onShare / onDone) as soon as the
 *   required steps are done, as there is no close button.
 *
 * `requireIdentification`: the caller already knows the KYC-AML form is
 * required (legacy flag); it is added as a required step when the landlord
 * has no tenant_requirements yet.
 */
const TenantRequirementsFlow = ({
  user,
  mode = "after",
  applicationId = null,
  autoApplication = false,
  landlordEmail = null,
  // Landlord email for the KYC-AML form (only a landlord with the form
  // active); defaults to landlordEmail
  identificationLandlordEmail = undefined,
  landlordId = null,
  workspaceId = null,
  // Also apply the tenant's own workspace requirements (landlord link flows)
  includeTenantWorkspace = false,
  workspace = null,
  requireIdentification = false,
  closable = true,
  // Already loaded /tenants/requirements-context response (skips the fetch)
  initialContext = null,
  // Signing as the guarantor of this tenant (KYC-AML step only)
  guarantorForTenantId = null,
  onShare = null,
  onClose = null,
  onIdentificationSigned = null,
  onDone = null,
}) => {
  const { _t } = useTranslator();
  const [context, setContext] = useState(null);
  const [steps, setSteps] = useState(null);
  const [resolvedMode, setResolvedMode] = useState(mode);
  const startedRef = useRef(false);
  const isAfterSharing = resolvedMode === "after";

  const finish = async (action) => {
    setSteps(null);
    await action?.();
  };
  // What ends the flow: the share before sharing, otherwise onDone / onClose
  const endAction = (afterSharing) =>
    afterSharing ? (onDone ?? onClose) : (onShare ?? onDone ?? onClose);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    (initialContext
      ? Promise.resolve({ data: { success_res: initialContext } })
      : axiosInstance.get(
          `${process.env.REACT_APP_SERVER_URL}/tenants/requirements-context`,
          {
            params: {
              application_id: applicationId ?? undefined,
              auto_application: autoApplication ? "true" : undefined,
              landlord_email: landlordEmail ?? undefined,
              landlord_id: landlordId ?? undefined,
              workspace_id: workspaceId ?? undefined,
              include_tenant_workspace: includeTenantWorkspace
                ? "true"
                : undefined,
              guarantor_for: guarantorForTenantId ?? undefined,
            },
          }
        )
    )
      .then(async (response) => {
        const data = response?.data?.success_res ?? {};
        const requirements = data.requirements ?? {};
        const household = requirements[TENANT_REQUIREMENTS.HOUSEHOLD];
        const identification =
          requirements[TENANT_REQUIREMENTS.CLIENT_IDENTIFICATION];

        const nextSteps = [];
        if (household?.active) {
          nextSteps.push({
            key: TENANT_REQUIREMENTS.HOUSEHOLD,
            requiredBeforeSharing: household.required_before_sharing,
            // Done when every question asked now is answered
            done: data.household_complete ?? Boolean(data.household_pets),
          });
        }
        // LinkedIn and the workspace's own questions. Required before sharing
        // when the workspace ticked Required on Applicant questions.
        if (data.applicant_questions) {
          nextSteps.push({
            key: APPLICANT_QUESTIONS_STEP,
            requiredBeforeSharing:
              data.applicant_questions.questions?.required === true,
            done: data.applicant_questions.complete === true,
          });
        }
        // Not asked when the workspace switched KYC off (requirements_config.kyc)
        if (
          !data.kyc_disabled &&
          (identification?.active || requireIdentification)
        ) {
          nextSteps.push({
            key: TENANT_REQUIREMENTS.CLIENT_IDENTIFICATION,
            requiredBeforeSharing: identification?.active
              ? identification.required_before_sharing
              : true,
            // Fresh from the backend: the user object is stale after signing
            done:
              data.identification_signed ??
              user?.consents?.client_identification_consent?.status ===
                constants.ACCEPTED,
          });
        }

        const afterSharing =
          mode === "after" || (mode === "auto" && data.shared === true);
        const pendingRequired = nextSteps.some(
          (step) => step.requiredBeforeSharing && !step.done
        );
        // Shown while any step is open. Before sharing, closing shares once
        // the required steps are done. A mandatory (no close) flow ends as
        // soon as the required steps are done.
        if (
          nextSteps.every((step) => step.done) ||
          (!closable && !pendingRequired)
        ) {
          await finish(endAction(afterSharing));
          return;
        }
        setResolvedMode(afterSharing ? "after" : "before");
        setContext(data);
        setSteps(nextSteps);
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || _t("somethingWentWrong"));
        onClose?.();
      });
  }, []);

  if (!steps || !context) {
    return createPortal(
      <div className="bsm-overlay">
        <div className="bsm-modal bsm-loading" role="status" aria-busy="true">
          <img src={SkorDefaultLoader} className="defaultSkorLoader" alt="" />
        </div>
      </div>,
      document.body
    );
  }

  const hasRequiredStepPending = (list) =>
    list.some((step) => step.requiredBeforeSharing && !step.done);

  const handleStepDone = async (stepKey) => {
    if (stepKey === TENANT_REQUIREMENTS.CLIENT_IDENTIFICATION) {
      await onIdentificationSigned?.();
    }
    const nextSteps = steps.map((step) =>
      step.key === stepKey ? { ...step, done: true } : step
    );
    const allDone = nextSteps.every((step) => step.done);
    if (allDone || (!closable && !hasRequiredStepPending(nextSteps))) {
      await finish(endAction(isAfterSharing));
      return;
    }
    setSteps(nextSteps);
  };

  const handleClose = async () => {
    if (!isAfterSharing && !hasRequiredStepPending(steps)) {
      await finish(endAction(false));
      return;
    }
    await finish(onClose);
  };

  const application = context.application;
  return (
    <BeforeSharingModal
      user={user}
      mode={resolvedMode}
      closable={closable}
      landlordName={context.landlord_name}
      steps={steps}
      memberStatuses={context.members}
      applicationId={application?._id ?? null}
      identificationApplicationId={
        // A guarantor signs through the guarantor / landlord path only
        !guarantorForTenantId && application?.client_identification_required
          ? application._id
          : null
      }
      applicationTitle={application?.title ?? null}
      minAdults={context.min_adults}
      household={context.household_pets}
      householdQuestions={context.household_questions ?? null}
      applicantQuestions={context.applicant_questions ?? null}
      workspace={workspace}
      landlordEmail={
        identificationLandlordEmail !== undefined
          ? identificationLandlordEmail
          : guarantorForTenantId
            ? context.identification_landlord_email
            : landlordEmail
      }
      guarantorForTenantId={guarantorForTenantId}
      onStepDone={handleStepDone}
      onClose={handleClose}
    />
  );
};

export default TenantRequirementsFlow;
